import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { sessionStorageKeys } from 'constants/common'
import styles from './index.module.scss'
import _compact from 'lodash/compact'
import ListCardBadge from './ListCardBadge'
import _upperFirst from 'lodash/upperFirst'
import _isEmpty from 'lodash/isEmpty'
import useProviderListingDetails from 'hooks/provider/useProviderListingDetails-v3'
import useAppSelector from 'hooks/useAppSelector'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { getCombinedAgeRange } from 'app/ProviderDetails/utils'
import { ageGroupServed } from 'app/Bookings/utils'
import useThriveAuth from 'hooks/useThriveAuth'
import {
  getParsedQuery,
  getPracticeAddressFeatures,
  getStringifiedQuery,
} from 'utils/common'
import { useRouter } from 'next/router'
import React, { useEffect, useState, useContext } from 'react'
import credentials from 'common/ProviderCredentials/credentials'
import { TimeZoneContext } from 'context/common/TimeZoneContext'

type ListCardProviderSectionProps = {
  providerDetails: Provider
  practice: Practice
  renderCustomInsuranceSection?: () => JSX.Element
}

const ListCardProviderSection: React.FC<ListCardProviderSectionProps> = ({
  providerDetails,
  practice,
  renderCustomInsuranceSection,
}) => {
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const router = useRouter()
  const { query } = router
  const [isAvailabilityInPerson, setIsAvailabilityInPerson] = useState<boolean>(
    false
  )

  const clientAgeGroupFilters =
    globalGroupedFilters[ProviderFilterTypes.CLIENT_AGE_GROUP]

  const timeZoneInSession = sessionStorage.getItem(
    sessionStorageKeys.TIMEZONE_ABBR
  )
  const { timeZoneObject } = useContext(TimeZoneContext)

  const {
    firstName,
    lastName,
    pronouns,
    profileImagePath,
    yearsOfExperience,
    title,
    clientAgeGroupIds,
    tagIds,
  } = providerDetails ?? {}
  const {
    availabilityTags,
    areaOfConcernValues,
    insuranceNamesValues,
  } = useProviderListingDetails(providerDetails, isSupportOrThirdPartyUser)
  const {
    city,
    state,
    streetAddress,
    aptOrSuite,
    zipcode,
  } = getPracticeAddressFeatures(practice)

  const providerCredentials = credentials[title] || {}
  const fullCredentials = providerCredentials?.definition
  const providerProfileLink = getProviderProfileRoute(providerDetails)
  const formattedQuery = getParsedQuery(router)
  const queryObject = {
    ...(query?.insuranceId && { insuranceId: query.insuranceId }),
    ...(practice?.distanceInMiles && {
      distanceInMiles: practice?.distanceInMiles?.toFixed(2),
    }),
    ...(!_isEmpty(formattedQuery?.counselingType) && {
      counselingType: formattedQuery.counselingType,
    }),
    ...(!_isEmpty(formattedQuery?.availabilityType) && {
      availabilityType: formattedQuery.availabilityType[0],
    }),
    ...(timeZoneInSession && {
      tzAbbr: timeZoneInSession,
    }),
    ...(isSupportOrThirdPartyUser && {
      tzAbbr: timeZoneObject?.tzAbbr,
      tzCode: timeZoneObject?.tzCode,
    }),
    ...(!_isEmpty(formattedQuery?.timeOfDay) && {
      timeOfDay: formattedQuery.timeOfDay,
    }),
  }

  const serializedQueryObject = getStringifiedQuery(queryObject)

  const availabilityTypeFilters =
    globalGroupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]

  useEffect(() => {
    availabilityTypeFilters.map((avail) => {
      if (
        avail.id === queryObject?.availabilityType &&
        avail.value === 'In Person'
      ) {
        setIsAvailabilityInPerson(true)
      }
    })
  }, [queryObject])

  const ageGroups = clientAgeGroupFilters
    .filter((clientAgeGroupFilter) =>
      clientAgeGroupIds?.includes(clientAgeGroupFilter.id)
    )
    .map((clientAgeGroupFilter) => clientAgeGroupFilter.value)

  const communityExperience = globalGroupedFilters[ProviderFilterTypes.TAG]
  const communityTag = communityExperience
    ?.filter((tag) => tagIds?.includes(tag.id))
    .map((tag) => tag.value)
  const capitalizedPronouns = pronouns?.map((pronoun) =>
    pronoun
      .split('/')
      .map((each) => each.charAt(0).toUpperCase() + each.slice(1))
      .join('/')
  )

  return (
    <div className={styles.ProviderCard}>
      <Link
        href={{
          pathname: providerProfileLink,
          query: serializedQueryObject,
        }}
        passHref
        legacyBehavior
      >
        <a href=" " className="text-decoration-none">
          <div className={styles.ProviderCard_container}>
            <div className={styles.ProviderCard_container_header_providerImage}>
              <Image
                unoptimized={!!profileImagePath}
                src={profileImagePath || DEFAULT_PROFILE_IMAGE_PATH}
                alt={`${firstName} ${lastName}'s profile image`}
                layout="fill"
                className="rounded-circle c-pointer"
                objectFit="cover"
                objectPosition="top"
              />
            </div>
            <div>
              <div className={styles.ProviderCard_container_header}>
                <div
                  className={styles.ProviderCard_container_header_providerInfo}
                >
                  <div
                    className={
                      styles.ProviderCard_container_header_providerInfo_container
                    }
                  >
                    <h3>
                      <span
                        className={
                          styles.ProviderCard_container_header_providerInfo_container_name
                        }
                      >
                        {`${firstName} ${lastName}`}{' '}
                      </span>
                      {!!pronouns?.length && (
                        <span
                          className={
                            styles.ProviderCard_container_header_providerInfo_container_pronouns
                          }
                        >
                          {capitalizedPronouns}
                        </span>
                      )}
                    </h3>
                  </div>

                  <p className={styles.credentials}>
                    {fullCredentials && (
                      <span>
                        {`${fullCredentials} ${
                          title !== fullCredentials ? ` (${title})` : ''
                        } `}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className={styles.InfoContainer}>
                {renderCustomInsuranceSection
                  ? renderCustomInsuranceSection()
                  : insuranceNamesValues.length > 0 && (
                      <div className={styles.InfoContainer_insurance}>
                        <div className={styles.InfoContainer_insurance_icon}>
                          <img
                            src="/icons/shield-check.svg"
                            srcSet="/icons/shield-check1.webp"
                            alt="insurance"
                          />
                        </div>
                        <div className={styles.InfoContainer_insurance_list}>
                          {providerDetails.insuranceNames.length > 4
                            ? `${insuranceNamesValues.slice(0, 4).join(', ')} ${
                                insuranceNamesValues[4]
                              }`
                            : insuranceNamesValues.join(', ')}
                        </div>
                      </div>
                    )}
                {areaOfConcernValues.length > 0 && (
                  <div className={styles.InfoContainer_speciality}>
                    <div className={styles.InfoContainer_speciality_icon}>
                      <img
                        src="/icons/armchair.svg"
                        srcSet="/icons/armchair.webp"
                        alt="speciality"
                      />
                    </div>
                    <div className={styles.InfoContainer_speciality_list}>
                      {providerDetails.areaOfConcernIds.length > 4
                        ? `${areaOfConcernValues.slice(0, 4).join(', ')} ${
                            areaOfConcernValues[4]
                          }`
                        : areaOfConcernValues.join(', ')}
                    </div>
                  </div>
                )}
                {isSupportOrThirdPartyUser && (
                  <div className={styles.InfoContainer_availability}>
                    <div className={styles.InfoContainer_availability_icon}>
                      <img
                        src="/icons/map-pin.svg"
                        srcSet="/icons/map-pin.webp"
                        alt="address"
                      />
                    </div>

                    <div className={styles.InfoContainer_availability_list}>
                      <div>
                        {_compact([streetAddress, aptOrSuite]).join(', ')}
                      </div>
                      <div>
                        {city}, {state} {zipcode} |{' '}
                        {`${practice?.distanceInMiles?.toFixed(2)}mi`}
                      </div>
                    </div>
                  </div>
                )}
                {isAvailabilityInPerson && !isSupportOrThirdPartyUser && (
                  <div className={styles.InfoContainer_availability}>
                    <div className={styles.InfoContainer_availability_icon}>
                      <img
                        src="/icons/map-pin.svg"
                        srcSet="/icons/map-pin.webp"
                        alt="address"
                      />
                    </div>

                    <div className={styles.InfoContainer_availability_list}>
                      <div>
                        {city}, {state} |{' '}
                        {`${practice?.distanceInMiles?.toFixed(2)}mi`}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.BadgeContainer}>
                {!_isEmpty(availabilityTags) && (
                  <ListCardBadge>
                    {_upperFirst(availabilityTags.join(' & '))}
                  </ListCardBadge>
                )}

                {!!yearsOfExperience && (
                  <ListCardBadge>{`${yearsOfExperience} years in practice`}</ListCardBadge>
                )}

                <ListCardBadge>
                  {`Client ages: ${getCombinedAgeRange(
                    ageGroupServed(ageGroups)
                  )}`}
                </ListCardBadge>

                {!_isEmpty(communityTag) && (
                  <ListCardBadge>LGBTQIA+ experience</ListCardBadge>
                )}
              </div>
            </div>
          </div>
        </a>
      </Link>
    </div>
  )
}

export default ListCardProviderSection
